/**
 * src/context/AuthContext.tsx — Bearer-token auth state.
 *
 * Model:
 *   - access_token:  short-lived JWT (24h default), in memory only, sent as
 *                    `Authorization: Bearer <...>` on every REST call.
 *   - refresh_token: long-lived JWT (30d default), persisted to localStorage
 *                    so `stay signed in` works across tab close / browser
 *                    restart. Used only against /AuthRefresh + /AuthLogout.
 *
 * No password is ever stored on the client. At boot we silently exchange
 * the refresh token for a fresh access token via harperClient's
 * configureOnUnauthorized handler. If refresh fails, the user drops to
 * `session_expired`.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useClerk } from '@clerk/clerk-react';
import { refresh as refreshTokens, logout as logoutRequest, AuthError } from '../api/authClient';
import type { TokenPair } from '../api/authClient';

const REFRESH_TOKEN_KEY = 'attic_refresh_token';
const CUSTOMER_EMAIL_KEY = 'attic_customer_email';

function decodeJwtExpiryMs(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2 || !parts[1]) return null;
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export type AuthState =
  | { status: 'restoring' }
  | { status: 'unauthenticated'; reason?: 'manual_sign_out' | 'session_expired' | undefined }
  | {
      status: 'authenticated';
      accessToken: string;
      refreshToken: string;
      customerEmail: string;
      accessExpiresAt: number;
    };

export interface AuthContextValue {
  status: AuthState['status'];
  isRestoring: boolean;
  isAuthenticated: boolean;
  /** Current access token. null when signed out / restoring. */
  accessToken: string | null;
  /** Backward-compat alias for accessToken. */
  token: string | null;
  customerEmail: string | null;
  unauthenticatedReason: 'manual_sign_out' | 'session_expired' | undefined;
  /** Called by LoginPage after /AuthLogin succeeds. */
  signIn: (tokens: TokenPair, customerEmail: string) => void;
  /**
   * Tear down the session. `scope: 'all'` revokes every refresh token for
   * the user server-side (Sign Out Everywhere); default `'device'` just
   * revokes this device's refresh token.
   */
  signOut: (scope?: 'device' | 'all') => Promise<void>;
  /** Drop to unauthenticated/session_expired. Used by the 401-retry handler. */
  markSessionExpired: () => void;
  /** Reset reason to undefined so the modal dismisses once user starts sign-in. */
  clearExpiredReason: () => void;
  /**
   * Called by harperClient's 401-retry path after a successful refresh so
   * the new access token propagates to every caller's next request without
   * re-render friction.
   */
  updateAccessToken: (newAccessToken: string, newAccessExpiresAtMs: number) => void;
  /** Synchronous getter for the current access token (used by harperClient). */
  getAccessToken: () => string | null;
  /** Synchronous getter for the current refresh token (used by harperClient). */
  getRefreshToken: () => string | null;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({ status: 'restoring' });
  const restoredRef = useRef(false);
  // Clerk's SDK exposes the Clerk instance here. Used in signOut() to
  // also end the Clerk session — without it, a user who "signs out"
  // from our UI would still be Clerk-authenticated on next page load,
  // and ClerkBridge would silently re-exchange them into a session
  // they thought they'd ended.
  const clerk = useClerk();

  // Mirror the live state in a ref so synchronous getters (getAccessToken /
  // getRefreshToken, called from non-React code in harperClient) always see
  // the latest value. Assign during render — not in useEffect — because
  // child effects run before parent effects, so a child fetching on the
  // same render that transitions auth would otherwise read a stale ref and
  // throw "Not authenticated" even with isAuthenticated=true. NativePushBridge
  // hit exactly this; direct assignment fixes it for any future callers too.
  const stateRef = useRef<AuthState>(state);
  stateRef.current = state;

  // -- Boot: silent refresh from stored refresh_token --------------------
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    const stored = typeof window !== 'undefined'
      ? window.localStorage.getItem(REFRESH_TOKEN_KEY)
      : null;
    const storedEmail = typeof window !== 'undefined'
      ? window.localStorage.getItem(CUSTOMER_EMAIL_KEY)
      : null;

    if (!stored || !storedEmail) {
      setState({ status: 'unauthenticated' });
      return;
    }

    // Fire-and-forget silent refresh. We don't await inside the effect to
    // avoid holding the mount — the state transition happens on resolution.
    (async () => {
      try {
        const res = await refreshTokens(stored);
        const accessExpiresAt = decodeJwtExpiryMs(res.access_token) ?? Date.now() + res.expires_in * 1000;
        setState({
          status: 'authenticated',
          accessToken: res.access_token,
          refreshToken: stored,
          customerEmail: storedEmail,
          accessExpiresAt,
        });
      } catch (err) {
        // Refresh rejected → stored refresh token is bad / expired / revoked.
        // Clear it and drop to session_expired so SessionExpiredOverlay shows.
        // Network errors also land here; treating them the same is imperfect
        // but we'd rather show a sign-in screen than a stuck "loading" state.
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(REFRESH_TOKEN_KEY);
          window.localStorage.removeItem(CUSTOMER_EMAIL_KEY);
        }
        const isRejected = err instanceof AuthError && err.type === 'refresh_rejected';
        setState({
          status: 'unauthenticated',
          reason: isRejected ? 'session_expired' : undefined,
        });
      }
    })();
  }, []);

  // -- State transitions ------------------------------------------------

  const signIn = useCallback((tokens: TokenPair, customerEmail: string) => {
    const accessExpiresAt = decodeJwtExpiryMs(tokens.access_token) ?? Date.now() + tokens.expires_in * 1000;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
      window.localStorage.setItem(CUSTOMER_EMAIL_KEY, customerEmail);
    }
    setState({
      status: 'authenticated',
      accessToken: tokens.access_token,
      refreshToken: tokens.refresh_token,
      customerEmail,
      accessExpiresAt,
    });
  }, []);

  const signOut = useCallback(async (scope: 'device' | 'all' = 'device') => {
    const current = stateRef.current;
    // Fire the server revoke first so the local clear doesn't race past it.
    // logoutRequest swallows network errors — local clear always succeeds.
    if (current.status === 'authenticated') {
      await logoutRequest(current.accessToken, current.refreshToken, scope);
    }
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(REFRESH_TOKEN_KEY);
      window.localStorage.removeItem(CUSTOMER_EMAIL_KEY);
    }
    // Tear down Clerk's session too so the next page load doesn't
    // silently re-bridge through ClerkBridge. Swallow any Clerk error —
    // if Clerk is unreachable or already signed out, our local state
    // clear above is still the source of truth for "signed out in this
    // browser."
    try { await clerk.signOut(); } catch { /* noop */ }
    setState({ status: 'unauthenticated', reason: 'manual_sign_out' });
  }, [clerk]);

  const markSessionExpired = useCallback(() => {
    setState((prev) => {
      if (prev.status === 'unauthenticated') return prev;
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(REFRESH_TOKEN_KEY);
        window.localStorage.removeItem(CUSTOMER_EMAIL_KEY);
      }
      return { status: 'unauthenticated', reason: 'session_expired' };
    });
  }, []);

  const clearExpiredReason = useCallback(() => {
    setState((prev) => {
      if (prev.status !== 'unauthenticated' || prev.reason !== 'session_expired') return prev;
      return { status: 'unauthenticated' };
    });
  }, []);

  const updateAccessToken = useCallback((newAccessToken: string, newAccessExpiresAtMs: number) => {
    setState((prev) => {
      if (prev.status !== 'authenticated') return prev;
      return { ...prev, accessToken: newAccessToken, accessExpiresAt: newAccessExpiresAtMs };
    });
  }, []);

  // Sync getters read from the ref so harperClient sees the latest tokens
  // even mid-refresh without threading state through useEffect rebinds.
  const getAccessToken = useCallback((): string | null => {
    const s = stateRef.current;
    return s.status === 'authenticated' ? s.accessToken : null;
  }, []);
  const getRefreshToken = useCallback((): string | null => {
    const s = stateRef.current;
    return s.status === 'authenticated' ? s.refreshToken : null;
  }, []);

  // Memoize so every useAuth() consumer doesn't re-render on every provider
  // render. State + the useCallback-stabilized fns are the only inputs.
  const value = useMemo<AuthContextValue>(() => ({
    status: state.status,
    isRestoring: state.status === 'restoring',
    isAuthenticated: state.status === 'authenticated',
    accessToken: state.status === 'authenticated' ? state.accessToken : null,
    token: state.status === 'authenticated' ? state.accessToken : null,
    customerEmail: state.status === 'authenticated' ? state.customerEmail : null,
    unauthenticatedReason: state.status === 'unauthenticated' ? state.reason : undefined,
    signIn,
    signOut,
    markSessionExpired,
    clearExpiredReason,
    updateAccessToken,
    getAccessToken,
    getRefreshToken,
  }), [
    state,
    signIn,
    signOut,
    markSessionExpired,
    clearExpiredReason,
    updateAccessToken,
    getAccessToken,
    getRefreshToken,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
